import * as React from "react";
import { Confirm } from "components/dialog";
import { $, media, merge } from "glamor";
import theme from "styles/theme";
import MenuItem from "material-ui/MenuItem";
import { formatMessage } from "lib/globalize-util";
import { List } from "immutable";
import { KINDS_TO_ALLOW, NONE, SHOW_EACHES_ON_HOVER } from "./constants";
import { PreviewPanelView } from "./preview-panel-view";
import { FormView } from "./form-view";
import { ITileSettings } from "./types";
import { DATATEST } from "constants/integration";

const styles = merge(
    {
        minHeight: 353,
        display: "flex",
        height: "460px",
        width: "100%",
        margin: "20px 0 50px 0",
        justifyContent: "space-evenly",
    },
    $(" > div", {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    }),
    $(" .helpText", {
        fontSize: "14px",
        color: theme.palette.darkGrey,
        marginBottom: "20px",
    }),
    media("(max-width: 768px)", {
        display: "block",
        height: "auto",
    })
);

interface IProps {
    onClose: () => void;
    onConfirm: (tileSettings: ITileSettings) => void;
    initialTileSettings: ITileSettings;
    itemDisplayProperty?: List<Map<string, any>>;
    hasAllocationDimensions: boolean;
}

export const ManageTileView: React.FunctionComponent<IProps> = ({
    onClose,
    onConfirm,
    itemDisplayProperty,
    initialTileSettings,
    hasAllocationDimensions,
}) => {
    const [tileSettings, setSettings] = React.useState<ITileSettings>(initialTileSettings);

    const handleCheckboxChange = (event, status) => {
        setSettings({
            ...tileSettings,
            [event.target.value]: status,
        });
    };

    React.useEffect(() => {
        if (tileSettings.showEachesOnHover && !tileSettings.showTotalCost && !tileSettings.showTotalRetail) {
            setSettings({
                ...tileSettings,
                [SHOW_EACHES_ON_HOVER]: false,
            });
        }
    }, [tileSettings]);

    const handleLineChange = (key) => (_event, _index, status) => {
        setSettings({
            ...tileSettings,
            [key]: status === NONE ? "" : status,
        });
    };

    const handleConfirm = () => {
        onConfirm(tileSettings);
    };

    const selectOptions = React.useMemo(() => {
        const options = itemDisplayProperty
            .filter((schemaKey) => KINDS_TO_ALLOW.includes(schemaKey.get("kind")))
            .map((schemaKey) => {
                const value = schemaKey.get("field_name");
                const label = schemaKey.get("display_name");
                return <MenuItem key={value} value={value} primaryText={label} />;
            })
            .toArray();

        options.unshift(<MenuItem key={NONE} value={NONE} primaryText={formatMessage("None")} />);

        return options;
    }, [itemDisplayProperty]);

    const hasAtLeastOneUpdate = Object.keys(tileSettings).some((key) => tileSettings[key] !== initialTileSettings[key]);
    const hasAtLeastOneItemSelected = Object.keys(tileSettings).some((key) => !!tileSettings[key]);

    const getTileSettingsValue = React.useMemo(() => {
        return Object.values(tileSettings).reduce(
            (target: {}, settingItem: string) => ({
                ...target,
                [settingItem]: itemDisplayProperty
                    .find((schemaKey) => schemaKey.get("field_name") === settingItem)
                    ?.get("display_name"),
            }),
            {}
        );
    }, [tileSettings, itemDisplayProperty]);

    return (
        <Confirm
            disabledConfirm={!hasAtLeastOneUpdate || !hasAtLeastOneItemSelected}
            contentStyle={{ maxWidth: "700px" }}
            title={formatMessage("Configure Tile View")}
            onConfirm={handleConfirm}
            onCancel={onClose}
            confirmLabel={formatMessage("Apply")}
            cancelLabel={formatMessage("Cancel")}
            open={true}
        >
            <div {...styles} data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.BODY}>
                <PreviewPanelView
                    hasAllocationDimensions={hasAllocationDimensions}
                    tileSettingsValue={getTileSettingsValue}
                    tileSettings={tileSettings}
                />
                <div>
                    <p className="helpText">
                        {formatMessage("Select the data points that you would like to display on each image tile")}:
                    </p>
                    <FormView
                        hasAllocationDimensions={hasAllocationDimensions}
                        tileSettings={tileSettings}
                        options={selectOptions}
                        onLineChange={handleLineChange}
                        onCheckboxChange={handleCheckboxChange}
                    />
                </div>
            </div>
        </Confirm>
    );
};
